import { useLazyContext } from "@redotech/react-util/context";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { EmailIntegrationsContext } from "@redotech/redo-merchant-support-app/services/email-integrations";
import { GeneralPermissions } from "@redotech/redo-model/permissions";
import { NotificationBannerItem } from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ActionRequiredSeverity } from "./action-required-notification";

export function useRevokedEmailPermissions(): {
  severity: ActionRequiredSeverity;
  item: NotificationBannerItem;
} | null {
  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);
  const navigate = useNavigate();
  const team = useContext(TeamContext);

  const canAuthenticateWithThirdParties = useHasPermission(
    GeneralPermissions.AUTHENTICATE_WITH_THIRD_PARTIES,
  );

  const revokedIntegrationInfo = useMemo(() => {
    if (!team?.settings.support?.enabled) {
      return { hasRevoked: false, emailType: null };
    }
    if (!emailIntegrationsLoad.value) {
      return { hasRevoked: false, emailType: null };
    }

    const hasRevokedGmail = (
      emailIntegrationsLoad.value.gmailIntegrations || []
    ).some((integration) => integration.permissionsRevoked);
    const hasRevokedOutlook = (
      emailIntegrationsLoad.value.outlookIntegrations || []
    ).some((integration) => integration.permissionsRevoked);

    const hasRevoked = hasRevokedGmail || hasRevokedOutlook;
    // If both have revoked permissions, prioritize Gmail (could be changed based on business logic)
    const emailType = hasRevokedGmail
      ? "gmail"
      : hasRevokedOutlook
        ? "outlook"
        : null;

    return { hasRevoked, emailType };
  }, [emailIntegrationsLoad.value, team?.settings.support?.enabled]);

  if (!canAuthenticateWithThirdParties) {
    return null;
  }

  if (!revokedIntegrationInfo.hasRevoked) {
    return null;
  }

  const handleReconnectClick = () => {
    navigate("/settings/channels/supportEmail");
  };

  return {
    severity: ActionRequiredSeverity.Medium,
    item: {
      id: "revoked-email-permissions",
      type: "warning",
      message:
        "Your email provider has revoked Redo's permissions. To continue receiving customer emails, please reconnect your email.",
      actions: [
        {
          actionType: "button",
          hierarchy: "primary",
          text: "Reconnect email",
          onClick: handleReconnectClick,
        },
      ],
    },
  };
}
